import { navigate } from '@/common/agents';

const TEAM_STUDIO_ROUTE_COVER_ID = 'roboneo-team-studio-route-cover';
const coverOwners = new WeakMap<HTMLElement, symbol>();

type TeamStudioNavigateOptions = Parameters<typeof navigate>[0];

/** 复用主题遮罩，仅允许最近一次导航清理它，避免迟到失败移除后续任务的遮罩。 */
function acquireTeamStudioRouteCover() {
  if (typeof document === 'undefined') return undefined;

  const existing = document.getElementById(TEAM_STUDIO_ROUTE_COVER_ID);
  const cover = existing || document.createElement('div');
  if (!existing) {
    const fallbackBackground = document.body.getAttribute('roboneo-data-theme') === 'light'
      ? '#eff1f4'
      : '#0a0a0a';
    cover.id = TEAM_STUDIO_ROUTE_COVER_ID;
    cover.style.cssText = `position:fixed;inset:0;z-index:10000;background:var(--background-canvas, ${fallbackBackground});pointer-events:none;`;
    document.body.appendChild(cover);
    window.setTimeout(() => cover.remove(), 5000);
  }

  const owner = Symbol('team-studio-navigation');
  coverOwners.set(cover, owner);
  /** 当前任务取消或派发失败时立即撤罩；后发导航已接管时保留它的遮罩。 */
  return () => {
    if (coverOwners.get(cover) !== owner) return;
    coverOwners.delete(cover);
    cover.remove();
  };
}

/** 在 Team Studio 整页跳转前覆盖当前页面，并等遮罩绘制后再执行跳转。 */
function withTeamStudioRouteCover(callback: () => void) {
  if (typeof document === 'undefined') {
    callback();
    return;
  }

  acquireTeamStudioRouteCover();

  if (!window.requestAnimationFrame) {
    callback();
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(callback);
  });
}

/** Team Studio 整页跳转前先绘制主题遮罩，再沿用现有 navigate 行为。 */
export function navigateWithTeamStudioCover(options: TeamStudioNavigateOptions) {
  withTeamStudioRouteCover(() => {
    navigate(options);
  });
}

/** 等待遮罩绘制并捕获派发异常；resolve 仅表示 void 导航已派发，不代表目标页面加载成功。 */
export function navigateWithTeamStudioCoverAsync(
  options: TeamStudioNavigateOptions,
  isCurrent: () => boolean,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let releaseCover: (() => void) | undefined;
    /** 每个延迟边界重新检查上下文，帧回调的异常必须回到调用方的 Promise。 */
    const runIfCurrent = (callback: () => void) => {
      try {
        if (!isCurrent()) throw new Error('Team Studio navigation context expired');
        callback();
      } catch (error) {
        releaseCover?.();
        reject(error);
      }
    };
    /** navigate 的正式返回类型是 void，不能把派发成功描述为页面加载成功。 */
    const dispatch = () => {
      navigate(options);
      resolve();
    };

    runIfCurrent(() => {
      releaseCover = acquireTeamStudioRouteCover();
      if (typeof document === 'undefined' || !window.requestAnimationFrame) {
        runIfCurrent(dispatch);
        return;
      }
      window.requestAnimationFrame(() => {
        runIfCurrent(() => {
          window.requestAnimationFrame(() => runIfCurrent(dispatch));
        });
      });
    });
  });
}
